import { use, useState } from "react";

import { useFocusMarketData } from "@/contexts/focus-market-data";
import { UserContext } from "@/contexts/user-context";
import type { DaoCoinStat } from "@/graphql/codegen/graphql";
import { useCoinPropertiesQuery } from "@/hooks/use-coin-properties-query";
import { useTokenMarketData } from "@/hooks/use-token-market-data";

import { usePreLaunchOverviewLogic } from "../../profile/use-prelaunch-overview-logic";

export const useTokenMetrics = (initialPublicKey?: string | null) => {
  const [publicKey, setPublicKey] = useState<string | null>(
    initialPublicKey ?? null,
  );
  const { currentUserPublicKey } = use(UserContext);

  const [selectedLaunchedToken, setSelectedLaunchedToken] =
    useState<Partial<DaoCoinStat> | null>(null);

  const { data: tokenDetails, isLoading: loadingCoinProperties } =
    useCoinPropertiesQuery(
      { publicKey: publicKey as string },
      { enabled: publicKey !== null },
    );

  const { loading: loadingExchangeRates, focusExchangeRate } =
    useFocusMarketData();

  const { data: tokenMarketData, loading: loadingTokenMarketData } =
    useTokenMarketData({
      publicKey: publicKey ?? "",
      ammPublicKey: tokenDetails?.coinProperties.AmmConfigs?.[0]?.AmmPublicKey,
      currentUserPublicKey,
      enabled: Boolean(
        publicKey && tokenDetails?.coinProperties.AmmConfigs?.[0]?.AmmPublicKey,
      ),
    });

  const tokenSummary =
    tokenMarketData?.tokenMarketVolumeSummaries?.nodes[0] ?? null;

  const {
    loadingTokenOrderbook,
    totalBidsUsd,
    totalBidsFocus,
    uniqueWallets,
    totalBids,
    tokenOrderbook,
    coinUserName,
  } = usePreLaunchOverviewLogic({
    tokenDetails,
    publicKey: publicKey,
    focusExchangeRate,
  });

  const specificCoinLoading =
    loadingCoinProperties ||
    loadingTokenOrderbook ||
    loadingExchangeRates ||
    loadingTokenMarketData;

  const username = tokenDetails?.coinProperties.Profile.Username ?? "";

  return {
    tokenSummary,
    selectedPublicKey: publicKey,
    setSelectedPublicKey: setPublicKey,
    tokenDetails,
    loadingTokenOrderbook,
    focusExchangeRate: focusExchangeRate ?? 0,
    totalBidsUsd,
    totalBidsFocus,
    uniqueWallets: uniqueWallets.filter(Boolean),
    totalBids,
    username,
    tokenOrderbook,
    coinUserName: coinUserName ?? "",
    specificCoinLoading,
    selectedLaunchedToken,
    setSelectedLaunchedToken,
  };
};
